import { call, put, takeLatest } from 'redux-saga/effects';
import api from '../../../utils/api';
import config from '../../../config';
import licenseService from '../../../services/licenseService';
import i18n from '../../../i18n';
import {
  fetchInit,
  fetchInitSuccess,
  fetchInitFailure,
  fetchLicenses,
  fetchLicensesSuccess,
  fetchLicensesFailure,
  fetchLicenseById,
  fetchLicenseByIdSuccess,
  fetchLicenseByIdFailure,
  addLicense,
  addLicenseSuccess,
  addLicenseFailure,
  updateLicense,
  updateLicenseSuccess,
  updateLicenseFailure,
  deleteLicense,
  deleteLicenseSuccess,
  deleteLicenseFailure,
  bulkDeleteLicenses,
  bulkDeleteLicensesSuccess,
  bulkDeleteLicensesFailure,
  approveLicense,
  approveLicenseSuccess,
  approveLicenseFailure,
  rejectLicense,
  rejectLicenseSuccess,
  rejectLicenseFailure,
  extendLicense,
  extendLicenseSuccess,
  extendLicenseFailure,
  revokeLicense,
  revokeLicenseSuccess,
  revokeLicenseFailure,
  fetchUnits,
  fetchUnitsSuccess,
  fetchUnitsFailure,
  assignUnit,
  assignUnitSuccess,
  assignUnitFailure,
  importLicenses,
  importLicensesSuccess,
  importLicensesFailure,
  exportLicenses,
  exportLicensesSuccess,
  exportLicensesFailure,
  downloadTemplate,
  downloadTemplateSuccess,
  downloadTemplateFailure,
} from './slice';

// Error message mapping
const ERROR_MESSAGES = {
  'License code already exists': 'licenses.errors.codeExists',
  'License key already exists': 'licenses.errors.keyExists',
  'License not found': 'licenses.errors.notFound',
  'Invalid date range': 'licenses.errors.invalidDateRange',
};

function* handleFetchInit() {
  try {
    const response = yield call(licenseService.getInit);
    yield put(fetchInitSuccess(response));
  } catch (error) {
    const errorMessage = error.message;
    yield put(fetchInitFailure(errorMessage));
  }
}

function* handleFetchLicenses(action) {
  try {
    // Get page, pageSize, sortColumn, sortDirection, filterCode, filterStatuses, filterLicenseTypes, filterUnits, and expiry date filters from action payload (if provided)
    const { page, pageSize, sortColumn, sortDirection, filterCode, filterStatuses, filterLicenseTypes, filterUnits, filterExpiryDateFrom, filterExpiryDateTo } = action.payload || {};
    const params = {};
    if (page) params.page = page;
    if (pageSize) params.pageSize = pageSize;
    if (sortColumn) {
      params.sortColumn = sortColumn;
      params.sortDirection = sortDirection || 'asc';
    }
    if (filterCode) {
      params.code = filterCode;
    }
    // Add expiry date filters
    if (filterExpiryDateFrom) {
      params.expiryDateFrom = filterExpiryDateFrom;
    }
    if (filterExpiryDateTo) {
      params.expiryDateTo = filterExpiryDateTo;
    }

    // Build query string for repeated params (status, licenseType, and unitId)
    let queryParts = [];
    if (filterStatuses && filterStatuses.length > 0) {
      queryParts.push(...filterStatuses.map(status => `status=${status}`));
    }
    if (filterLicenseTypes && filterLicenseTypes.length > 0) {
      queryParts.push(...filterLicenseTypes.map(licenseType => `licenseType=${licenseType}`));
    }
    if (filterUnits && filterUnits.length > 0) {
      queryParts.push(...filterUnits.map(unitId => `unitId=${unitId}`));
    }

    // Combine base URL with params and query string
    let url = config.endpoints.licenses.list;

    // Add regular params as query string
    const regularParams = new URLSearchParams(params).toString();
    if (regularParams) {
      url += `?${regularParams}`;
    }

    // Add repeated params
    if (queryParts.length > 0) {
      const separator = regularParams ? '&' : '?';
      url += `${separator}${queryParts.join('&')}`;
    }

    const response = yield call(api.get, url);
    // Pass the whole response (including pagination info)
    yield put(fetchLicensesSuccess(response));
  } catch (error) {
    const errorMessage = ERROR_MESSAGES[error.message] || error.message;
    yield put(fetchLicensesFailure(errorMessage));
  }
}

function* handleFetchLicenseById(action) {
  try {
    const response = yield call(licenseService.getLicenseById, action.payload);
    yield put(fetchLicenseByIdSuccess(response));
  } catch (error) {
    const errorMessage = ERROR_MESSAGES[error.message] || error.message;
    yield put(fetchLicenseByIdFailure(errorMessage));
  }
}

function* handleAddLicense(action) {
  try {
    const response = yield call(licenseService.createLicense, action.payload);
    yield put(addLicenseSuccess(response));
  } catch (error) {
    const errorMessage = ERROR_MESSAGES[error.message] || error.message;
    yield put(addLicenseFailure(errorMessage));
  }
}

function* handleUpdateLicense(action) {
  try {
    const response = yield call(licenseService.updateLicense, action.payload);
    yield put(updateLicenseSuccess(response));
  } catch (error) {
    const errorMessage = ERROR_MESSAGES[error.message] || error.message;
    yield put(updateLicenseFailure(errorMessage));
  }
}

function* handleDeleteLicense(action) {
  try {
    const response = yield call(licenseService.deleteLicense, action.payload);
    yield put(deleteLicenseSuccess(response));
  } catch (error) {
    const errorMessage = ERROR_MESSAGES[error.message] || error.message;
    yield put(deleteLicenseFailure(errorMessage));
  }
}

function* handleBulkDeleteLicenses(action) {
  try {
    const response = yield call(licenseService.bulkDeleteLicenses, action.payload);
    yield put(bulkDeleteLicensesSuccess(response));
  } catch (error) {
    const errorMessage = ERROR_MESSAGES[error.message] || error.message;
    yield put(bulkDeleteLicensesFailure(errorMessage));
  }
}

function* handleApproveLicense(action) {
  try {
    const response = yield call(licenseService.approveLicense, action.payload);
    yield put(approveLicenseSuccess(response));
  } catch (error) {
    const errorMessage = ERROR_MESSAGES[error.message] || error.message;
    yield put(approveLicenseFailure(errorMessage));
  }
}

function* handleRejectLicense(action) {
  try {
    const { id, statusNote } = action.payload;
    const response = yield call(licenseService.rejectLicense, id, statusNote);
    yield put(rejectLicenseSuccess(response));
  } catch (error) {
    const errorMessage = ERROR_MESSAGES[error.message] || error.message;
    yield put(rejectLicenseFailure(errorMessage));
  }
}

function* handleExtendLicense(action) {
  try {
    const { id, newExpiryDate } = action.payload;
    const response = yield call(licenseService.extendLicense, id, newExpiryDate);
    yield put(extendLicenseSuccess(response));
  } catch (error) {
    const errorMessage = ERROR_MESSAGES[error.message] || error.message;
    yield put(extendLicenseFailure(errorMessage));
  }
}

function* handleRevokeLicense(action) {
  try {
    const { id, statusNote } = action.payload;
    const response = yield call(licenseService.revokeLicense, id, statusNote);
    yield put(revokeLicenseSuccess(response));
  } catch (error) {
    const errorMessage = ERROR_MESSAGES[error.message] || error.message;
    yield put(revokeLicenseFailure(errorMessage));
  }
}

function* handleFetchUnits(action) {
  try {
    const { page, pageSize } = action.payload || {};
    const params = {};
    if (page) params.page = page;
    if (pageSize) params.pageSize = pageSize;

    const response = yield call(api.get, config.endpoints.units.list, params);
    yield put(fetchUnitsSuccess(response));
  } catch (error) {
    const errorMessage = error.message;
    yield put(fetchUnitsFailure(errorMessage));
  }
}

function* handleAssignUnit(action) {
  try {
    const response = yield call(licenseService.assignUnit, action.payload);
    yield put(assignUnitSuccess(response));
  } catch (error) {
    const errorMessage = error.message;
    yield put(assignUnitFailure(errorMessage));
  }
}

function* handleImportLicenses(action) {
  try {
    const response = yield call(licenseService.importLicenses, action.payload);
    console.log('Import response:', response);

    const successCount = response.successCount || 0;
    const failureCount = response.failureCount || 0;
    const totalRows = response.totalRows || 0;
    const errors = response.errors || [];

    // Build message
    let message = i18n.t('licenses.import.completed', { successCount, totalRows });

    if (failureCount > 0) {
      message += `, ${failureCount} ${i18n.t('licenses.import.failed')}`;
    }

    if (failureCount > 0 && errors.length > 0) {
      console.error('Import errors:', errors);
      // Show first 3 errors
      const errorSummary = errors.slice(0, 3).join('\n');
      message += `\n\n${i18n.t('licenses.import.errors')}\n${errorSummary}`;
      if (errors.length > 3) {
        message += `\n${i18n.t('licenses.import.moreErrors', { count: errors.length - 3 })}`;
      }
    }

    // Determine if this should be success or failure notification
    if (failureCount > 0 && successCount === 0) {
      // All failed - show as error
      yield put(importLicensesFailure(message));
    } else if (failureCount > 0) {
      // Partial success - show as error with details
      yield put(importLicensesFailure(message));
      // Still refresh the list since some licenses were imported
      yield put(fetchLicenses({
        page: 1,
        pageSize: 10,
        sortColumn: 'code',
        sortDirection: 'asc'
      }));
    } else {
      // All succeeded - show as success
      yield put(importLicensesSuccess({ ...response, message }));
      // Refresh licenses list
      yield put(fetchLicenses({
        page: 1,
        pageSize: 10,
        sortColumn: 'code',
        sortDirection: 'asc'
      }));
    }
  } catch (error) {
    console.error('Import error:', error);
    let errorMessage;

    if (error.response && error.response.data) {
      errorMessage = error.response.data.message || error.response.data.error;
    } else if (error.message) {
      errorMessage = error.message;
    }

    yield put(importLicensesFailure(errorMessage));
  }
}

function* handleExportLicenses(action) {
  try {
    // action.payload contains: exportType ('excel' or 'pdf') and filters
    const { exportType, filters } = action.payload;

    if (exportType === 'excel') {
      yield call(licenseService.exportToExcel, filters);
    } else if (exportType === 'pdf') {
      yield call(licenseService.exportToPdf, filters);
    }

    yield put(exportLicensesSuccess());
  } catch (error) {
    const errorMessage = error.message;
    yield put(exportLicensesFailure(errorMessage));
  }
}

function* handleDownloadTemplate() {
  try {
    yield call(licenseService.downloadTemplate);
    yield put(downloadTemplateSuccess());
  } catch (error) {
    const errorMessage = error.message;
    yield put(downloadTemplateFailure(errorMessage));
  }
}

export function* watchLicensesActions() {
  yield takeLatest(fetchInit.type, handleFetchInit);
  yield takeLatest(fetchLicenses.type, handleFetchLicenses);
  yield takeLatest(fetchLicenseById.type, handleFetchLicenseById);
  yield takeLatest(addLicense.type, handleAddLicense);
  yield takeLatest(updateLicense.type, handleUpdateLicense);
  yield takeLatest(deleteLicense.type, handleDeleteLicense);
  yield takeLatest(bulkDeleteLicenses.type, handleBulkDeleteLicenses);
  yield takeLatest(approveLicense.type, handleApproveLicense);
  yield takeLatest(rejectLicense.type, handleRejectLicense);
  yield takeLatest(extendLicense.type, handleExtendLicense);
  yield takeLatest(revokeLicense.type, handleRevokeLicense);
  yield takeLatest(fetchUnits.type, handleFetchUnits);
  yield takeLatest(assignUnit.type, handleAssignUnit);
  yield takeLatest(importLicenses.type, handleImportLicenses);
  yield takeLatest(exportLicenses.type, handleExportLicenses);
  yield takeLatest(downloadTemplate.type, handleDownloadTemplate);
}
